import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy, Component, ElementRef, HostListener, Inject, ViewChild, computed, effect,
} from '@angular/core';
import { VideoModalService } from './video-modal.service';

/**
 * The video pop-up: a dimmed backdrop, a 16:9 player and a round close
 * button. Opened through VideoModalService from the Media & Blog video tiles
 * and banner, and from the home page's Blogs And Media video.
 *
 * The player is the YouTube embed for a YouTube video, or a native <video>
 * with controls for an MP4 uploaded through the CMS. Either only exists while
 * the pop-up is open, so nothing loads before the viewer asks for a video,
 * and closing it stops playback. Autoplay is on because the only way in is a
 * click.
 *
 * Closes on the button, on Escape and on a click on the backdrop. While open
 * the page behind does not scroll, and focus goes to the close button and
 * returns to whatever opened the pop-up once it closes.
 */
@Component({
  selector: 'app-video-modal',
  templateUrl: './video-modal.component.html',
  styleUrls: ['./video-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VideoModalComponent {
  readonly video = this.modal.video;

  /**
   * Built from an ID VideoModalService has already validated, on a fixed
   * host. youtube-nocookie keeps YouTube's cookies off until playback starts;
   * playsinline stops iOS from forcing its own fullscreen player.
   */
  readonly embedUrl = computed(() => {
    const item = this.video();
    return item?.kind === 'youtube'
      ? `https://www.youtube-nocookie.com/embed/${item.videoId}?autoplay=1&rel=0&playsinline=1`
      : null;
  });

  @ViewChild('closeButton') private closeButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('mp4Player') private mp4Player?: ElementRef<HTMLVideoElement>;

  /** What had focus when the pop-up opened, to hand it back on close. */
  private returnFocus: HTMLElement | null = null;
  /** The body's own overflow before the pop-up locked it. */
  private previousOverflow = '';

  constructor(
    private modal: VideoModalService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    // Only ever opened by a click, so this never runs a DOM write on the server.
    effect(() => {
      if (this.video()) {
        this.onOpen();
      } else {
        this.onClose();
      }
    });
  }

  close(): void {
    this.stopMp4();
    this.modal.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.video()) {
      this.close();
    }
  }

  private onOpen(): void {
    if (!this.returnFocus) {
      this.returnFocus = this.document.activeElement as HTMLElement | null;
      this.previousOverflow = this.document.body.style.overflow;
      this.document.body.style.overflow = 'hidden';
    }
    // After this render, once the button (and an MP4's <video>) is in the DOM.
    setTimeout(() => {
      this.closeButton?.nativeElement.focus({ preventScroll: true });
      // `autoplay` normally starts it; this covers a browser that waited for
      // a play() call. A refusal leaves the controls for the viewer to press.
      const video = this.mp4Player?.nativeElement;
      if (video?.paused) { video.play()?.catch(() => { /* controls remain */ }); }
    });
  }

  /**
   * Stops an MP4 and lets go of its download before the element goes away.
   * Taking the element out of the page pauses it too, but emptying the src
   * also ends the (possibly 100 MB) transfer instead of leaving it to the
   * garbage collector.
   */
  private stopMp4(): void {
    const video = this.mp4Player?.nativeElement;
    if (!video) { return; }
    video.pause();
    video.removeAttribute('src');
    video.load();
  }

  private onClose(): void {
    if (!this.returnFocus) {
      return;
    }
    this.document.body.style.overflow = this.previousOverflow;
    const target = this.returnFocus;
    this.returnFocus = null;
    target.focus?.({ preventScroll: true });
  }
}
